import { DownOutlined } from '@ant-design/icons';
import { Divider } from 'antd';
import { useMemo, useState } from 'react';
import styled, { css } from 'styled-components';

import theme from '../../../assets/theme';
import { LessonAttachment } from '../../../services/backendService/backendService';
import { parseSlateStringToText } from '../../SlideComponents/slateUtil';
import { Slide } from '../../SlideComponents/slide.types';
import { Button } from '../Button';

const CheckboxIcon = ({ isSelected }: { isSelected: boolean }) => {
  return (
    <CheckContainer $isSelected={isSelected}>
      <svg
        width="20"
        height="20"
        viewBox="0 0 20 20"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          fillRule="evenodd"
          clipRule="evenodd"
          d="M4 0C1.79086 0 0 1.79086 0 4V16C0 18.2091 1.79086 20 4 20H16C18.2091 20 20 18.2091 20 16V4C20 1.79086 18.2091 0 16 0H4ZM17.6179 6.50021C18.1703 5.88283 18.1176 4.93454 17.5002 4.38215C16.8828 3.82976 15.9345 3.88244 15.3822 4.49982L7.87909 12.8856L4.49656 9.8789C3.87739 9.32852 2.92928 9.38429 2.3789 10.0035C1.82853 10.6226 1.8843 11.5708 2.50347 12.1211L7.00347 16.1211C7.62122 16.6702 8.56676 16.6162 9.11788 16.0002L17.6179 6.50021Z"
          fill="#61B195"
        />
      </svg>
    </CheckContainer>
  );
};

const CheckContainer = styled.div<{ $isSelected: boolean }>`
  width: 20px;
  height: 20px;
  border: 1px solid ${theme.colors.grey};
  border-radius: 5px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
  transition: all 0.2s ease-in-out;
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      border: 0px solid ${theme.colors.grey};
    `}

  svg {
    margin-right: 0;
    height: 20px;
    width: 20px;
    transition: all 0.2s ease-in-out;

    ${({ $isSelected }) =>
      !$isSelected &&
      css`
        height: 0px;
        width: 0px;
      `}
  }
`;

export type SlidesToTranslate = {
  name: string;
  slideId: string;
}[];
export type AttachmentsToTranslate = {
  name: string;
  attachmentId: string;
}[];

const TranslateMultiSelect = ({
  slidesToTranslate,
  selectableSlides,
  onSelect,
  selectTranslateAllSlides,
  isTranslateAllSlides,
  totalTranslatableSlides,
  selectedSlide,
}: {
  slidesToTranslate: SlidesToTranslate;
  selectableSlides: {
    displayName: string;
    slideId: string | number | boolean;
    noContent?: boolean;
  }[];
  onSelect?: (value: string | number | boolean) => void;
  selectTranslateAllSlides: () => void;
  isTranslateAllSlides: boolean;
  totalTranslatableSlides: number;
  selectedSlide: Slide | undefined;
}) => {
  const [dropdownIsOpen, setDropdownIsOpen] = useState(false);
  const selectedSlideHasContent = useMemo(() => {
    return selectedSlide?.elements.some(
      element => element.type === 'text' && element.textValue,
    );
  }, [selectedSlide]);
  const currentSlideIndex = useMemo(
    () =>
      selectableSlides.findIndex(slide => {
        return slide.slideId === selectedSlide?.id;
      }) + 1,
    [selectableSlides, selectedSlide?.id],
  );

  const currentSlideIsSelected = slidesToTranslate?.some(
    slide => slide.slideId === selectableSlides[currentSlideIndex - 1]?.slideId,
  );

  const overlayOptions = (
    <DropdownStyleContainer>
      <div
        style={{
          position: 'relative',
          height: '100%',
          overflowY: 'auto',
          maxHeight: 200,
        }}
      >
        <OptionButton
          $isSelected={isTranslateAllSlides}
          type="ghost"
          key={'allSlides'}
          onClick={() => selectTranslateAllSlides()}
          icon={<CheckboxIcon isSelected={!!isTranslateAllSlides} />}
        >
          All slides ({totalTranslatableSlides} slide
          {totalTranslatableSlides > 1 ? 's' : ''})
        </OptionButton>
        <OptionButton
          $isSelected={currentSlideIsSelected}
          type="ghost"
          key={'currentSlide'}
          onClick={() => {
            if (selectedSlideHasContent) {
              onSelect && onSelect(selectableSlides[currentSlideIndex - 1].slideId);
            }
          }}
          icon={<CheckboxIcon isSelected={currentSlideIsSelected} />}
        >
          {selectedSlideHasContent && <> Current slide (slide {currentSlideIndex})</>}
          {!selectedSlideHasContent && <> {`Current slide: (No text to translate)`}</>}
        </OptionButton>
        <Divider style={{ margin: 0 }} />
        {selectableSlides.map(option => {
          const isSelected = slidesToTranslate?.some(
            slide => slide.slideId === option.slideId,
          );
          return (
            <OptionButton
              $isSelected={isSelected && !option.noContent}
              type="ghost"
              key={option.slideId.toString()}
              onClick={() => {
                if (!option.noContent) {
                  onSelect && onSelect(option.slideId);
                }
              }}
              icon={<CheckboxIcon isSelected={!!isSelected && !option.noContent} />}
            >
              {option.noContent && (
                <>
                  {option.displayName}: {`(No text to translate)`}
                </>
              )}
              {!option.noContent && option.displayName}
            </OptionButton>
          );
        })}
      </div>
      <Button
        styles={{ width: 'calc(100% - 10px)', margin: '10px 5px 5px 5px' }}
        onClick={() => setDropdownIsOpen(false)}
      >
        Done
      </Button>
    </DropdownStyleContainer>
  );

  const formatDropdownButtonText = (slidesToTranslate: any[]) => {
    if (slidesToTranslate.length === 0) {
      return '';
    }

    //find the selected slide numbers and sort them
    const selectedItems = slidesToTranslate
      .map(slide => parseInt(slide.name.match(/\d+/)[0]))
      .sort((a, b) => a - b);
    const ranges = [];
    let currentRange = [selectedItems[0]];

    //place the slide numbers into ranges of consecutive numbers (e.g. [1,2,3,4,5] => [1-5])
    for (let i = 1; i < selectedItems.length; i++) {
      if (selectedItems[i] === selectedItems[i - 1] + 1) {
        currentRange.push(selectedItems[i]);
      } else {
        ranges.push(currentRange);
        currentRange = [selectedItems[i]];
      }
    }
    ranges.push(currentRange);

    //where there is more than one slide in a range add a hyphen between the first and last slide
    const formattedRanges = ranges.map(range =>
      range.length === 1 ? `${range[0]}` : `${range[0]}-${range[range.length - 1]}`,
    );
    //where there is more than one range add a comma between the ranges and an ampersand between the last two ranges
    const result = formattedRanges.join(', ').replace(/, ([^,]*)$/, ' & $1');

    return slidesToTranslate.length === 1 ? `Slide ${result}` : `Slides ${result}`;
  };

  const selectedOptions = formatDropdownButtonText(slidesToTranslate);
  return (
    <div style={{ position: 'relative' }}>
      <Label>Select slides to translate</Label>
      <MainButton
        type="ghost"
        icon={<DownOutlined />}
        onClick={() => setDropdownIsOpen(!dropdownIsOpen)}
      >
        {selectedOptions.length === 0 ? 'Select slides' : selectedOptions}
      </MainButton>
      {dropdownIsOpen && overlayOptions}
    </div>
  );
};

const Label = styled.div`
  color: ${theme.colors.black};
  font-family: 'Nunito';
  font-style: normal;
  font-weight: 600;
  font-size: 12px;
  line-height: 18px;
  margin-bottom: 4px;
`;

const MainButton = styled(Button)`
  border: none;
  box-shadow: none;
  padding: 10px 8px;
  font-size: 14px;
  border: ${theme.colors.grey} 1px solid;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  span {
    display: flex;
    font-size: 16px;
    font-weight: 400;
  }
  svg {
    height: 21px;
    width: 11px;
    margin-right: 2px;
    color: ${theme.colors.grey};
  }
`;

const OptionButton = styled(Button)<{ $isSelected?: boolean }>`
  border: none;
  box-shadow: none;
  padding: 10px 8px;
  font-size: 14px;
  border: none;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row-reverse;
  span {
    display: flex;
    font-size: 16px;
    font-weight: 400;
    max-width: 80%;
    overflow: hidden;
    text-overflow: ellipsis;
    text-align: left !important;

    @supports (-webkit-line-clamp: 1) {
      // adds ellipsis for webkit browsers. Other browser still cuts, but adds no ellipssis.
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: initial;
      display: -webkit-box;
      -webkit-line-clamp: 1;
      -webkit-box-orient: vertical;
    }
  }
  &:hover {
    background-color: ${theme.colors.grey};
  }
  ${({ $isSelected }) =>
    $isSelected &&
    css`
      &:hover {
        background-color: ${theme.colors.grey};
      }
    `}
`;

const DropdownStyleContainer = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  width: 100%;
  z-index: 100;
  border-radius: 4px;
  background-color: ${theme.colors.white};
  box-shadow: 0px 4px 10px rgba(0, 0, 0, 0.25);
`;

export default TranslateMultiSelect;
